import { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

/**
 * Custom hook to sync shared filters with URL query parameters
 * This allows filters to persist when switching between Users and Subscriptions tabs
 */
const useSharedFilters = () => {
  const location = useLocation();
  const history = useHistory();

  // Parse initial values from URL
  const getInitialValue = (param, defaultValue = null) => {
    const searchParams = new URLSearchParams(location.search);
    const value = searchParams.get(param);
    return value !== null ? value : defaultValue;
  };

  // Shared filter states (initialized from URL)
  const [email, setEmailState] = useState(() => getInitialValue('email'));
  const [name, setNameState] = useState(() => getInitialValue('name'));
  const [startDate, setStartDateState] = useState(() => getInitialValue('startDate'));
  const [finishDate, setFinishDateState] = useState(() => getInitialValue('finishDate'));

  // Update URL when filters change
  const updateUrlParams = useCallback((params) => {
    const searchParams = new URLSearchParams(location.search);

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== '' && value !== undefined) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });

    const newSearch = searchParams.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

    // Use replace to avoid creating too many history entries
    history.replace(newUrl);
  }, [location.pathname, location.search, history]);

  // Setters that also update URL
  const setEmail = useCallback((value) => {
    setEmailState(value);
    updateUrlParams({ email: value });
  }, [updateUrlParams]);

  const setName = useCallback((value) => {
    setNameState(value);
    updateUrlParams({ name: value });
  }, [updateUrlParams]);

  const setStartDate = useCallback((value) => {
    setStartDateState(value);
    updateUrlParams({ startDate: value });
  }, [updateUrlParams]);

  const setFinishDate = useCallback((value) => {
    setFinishDateState(value);
    updateUrlParams({ finishDate: value });
  }, [updateUrlParams]);

  // Sync state from URL when location changes (e.g., when navigating back)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlEmail = searchParams.get('email');
    const urlName = searchParams.get('name');
    const urlStartDate = searchParams.get('startDate');
    const urlFinishDate = searchParams.get('finishDate');

    if (urlEmail !== email) setEmailState(urlEmail);
    if (urlName !== name) setNameState(urlName);
    if (urlStartDate !== startDate) setStartDateState(urlStartDate);
    if (urlFinishDate !== finishDate) setFinishDateState(urlFinishDate);
  }, [location.search]);

  return {
    email,
    setEmail,
    name,
    setName,
    startDate,
    setStartDate,
    finishDate,
    setFinishDate,
  };
};

export default useSharedFilters;
